import network from '@/services/network';
import {
  CockpitAntraegeResponse,
  CockpitAufschluesselung,
  CockpitAuslastungResponse,
  CockpitSprechstundeResponse,
  CockpitTerminverteilungResponse,
  CockpitTerminverteilungTyp,
  CockpitMetaKampagnenResponse,
  CockpitAufschluesselungParams,
  CockpitBelieferungResponse,
  CockpitBuchungsquellenResponse,
  CockpitCloserRookieResponse,
  CockpitCasesResponse,
  CockpitDrilldownKey,
  CockpitDrilldownResponse,
  CockpitEbVerteilungResponse,
  CockpitSicht,
  CockpitMeetingsResponse,
  CockpitParams,
  CockpitTelefonieParams,
  CockpitTelefonieResponse,
  CockpitVerlaufParams,
  CockpitVerlaufResponse,
  CockpitReklaquoteParams,
  CockpitReklaquoteResponse,
  CockpitRookieTelefonieParams,
  CockpitRookieTelefonieResponse,
  CockpitRookieTelefonieVerlaufResponse,
  CockpitNoShowVpParams,
  CockpitNoShowVpResponse,
  CockpitNoShowVpFaelleResponse,
} from '@/types/cockpit';
import { CockpitInaktivSchalter } from '@/types/cockpitInaktiv';
import { ErrorResponse } from '@/utils';

/**
 * KPI-Cockpit fuer Fuehrungskraefte (Auftrag Alex 09.08.2026).
 * Alle Endpunkte verlangen `dashboard.leadership.view`.
 */

/**
 * Gemeinsame Query-Parameter. `scope_id` geht nur mit, wenn es gesetzt ist —
 * ein mitgeschicktes `scope_id=null` wuerde die Validierung bei scope=own
 * unnoetig belasten.
 */
/**
 * Schalter „Inaktive anzeigen" (Alex 23.10.2026) — die EINE Stelle, an der er
 * zum Query-Parameter wird. Immer explizit gesendet: das Backend faellt ohne
 * Parameter auf „mit Inaktiven" zurueck, der Standard der Seite ist aber AUS.
 */
const inaktivParam = (params: CockpitInaktivSchalter) => ({
  include_inactive: params.includeInactive ? 1 : 0,
});

const cockpitParams = (params: CockpitParams) => ({
  from: params.from,
  to: params.to,
  ...(params.scope ? { scope: params.scope } : {}),
  ...(params.scopeId != null ? { scope_id: params.scopeId } : {}),
  ...inaktivParam(params),
});

const getMeetings = async (params: CockpitParams): Promise<{ data: CockpitMeetingsResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitMeetingsResponse }>(
      '/dashboard/cockpit/meetings',
      { params: cockpitParams(params) },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getCases = async (params: CockpitParams): Promise<{ data: CockpitCasesResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitCasesResponse }>(
      '/dashboard/cockpit/cases',
      { params: cockpitParams(params) },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Kachel „EB-Verteilung" (Umbau Alex 13.08.2026): zwei Zeitachsen, fünf
 * Herkunfts-Segmente, Sichten-Umschalter Team/Pool.
 *
 * `sicht` geht nur mit, wenn sie gesetzt ist — ohne den Parameter liefert das
 * Backend die Team-Sicht. So bleibt der Aufruf ohne Umschalter unverändert.
 */
const getEbVerteilung = async (
  params: CockpitParams & { sicht?: CockpitSicht },
): Promise<{ data: CockpitEbVerteilungResponse }> => {
  try {
    const { sicht, ...rest } = params;
    const { data } = await network.get<{ data: CockpitEbVerteilungResponse }>(
      '/dashboard/cockpit/eb-verteilung',
      { params: { ...cockpitParams(rest), ...(sicht ? { sicht } : {}) } },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Kachel „Anträge erstellt" (Auftrag Alex 21.10.2026): Vorgänge der
 * Antragserstellung nach Erstelldatum, je Team + Berater-Liste.
 */
const getAntraege = async (
  params: CockpitParams,
): Promise<{ data: CockpitAntraegeResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitAntraegeResponse }>(
      '/dashboard/cockpit/antraege',
      { params: cockpitParams(params) },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Kachel „Buchungsquellen" (Auftrag Alex 10.08.2026). */
const getBuchungsquellen = async (
  params: CockpitParams,
): Promise<{ data: CockpitBuchungsquellenResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitBuchungsquellenResponse }>(
      '/dashboard/cockpit/buchungsquellen',
      { params: cockpitParams(params) },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Kachel „Belieferung" (Auftrag Alex 23.08.2026, Punkt 5).
 *
 * `from`/`to` gehen mit, weil alle Cockpit-Endpunkte sie verlangen — die
 * Antwort ist aber STICHTAGSBEZOGEN und ignoriert sie (Begründung am
 * Backend-Docblock). Sie trägt dafür `stichtag`.
 */
const getBelieferung = async (
  params: CockpitParams,
): Promise<{ data: CockpitBelieferungResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitBelieferungResponse }>(
      '/dashboard/cockpit/belieferung',
      { params: cockpitParams(params) },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};


/** Sektion „Closer vs. Rookie" (Auftrag Alex 21.08.2026, Nachtrag 2). */
const getCloserRookie = async (
  params: CockpitParams,
): Promise<{ data: CockpitCloserRookieResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitCloserRookieResponse }>(
      '/dashboard/cockpit/closer-rookie',
      { params: cockpitParams(params) },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Kachel „Kalenderauslastung" (Auftrag Alex 10.08.2026, Weg 1).
 *
 * Eigener Aufruf und im Frontend NACHGELADEN: sie ist die teuerste Abfrage der
 * Seite (gemessen 370 ms bei 30 Tagen, 2,5 s bei 90, 11,7 s bei einem Jahr —
 * sie rechnet ein Tagesraster gegen 42 000 Kalendereintraege). Im Promise.all
 * der Sektionen haette sie alle anderen Zahlen so lange zurueckgehalten.
 */
const getKalenderauslastung = async (
  params: CockpitParams,
): Promise<{ data: CockpitAuslastungResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitAuslastungResponse }>(
      '/dashboard/cockpit/kalenderauslastung',
      { params: cockpitParams(params) },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Sektion „Sprechstunde" (Auftrag Alex 06.10.2026): Buchungen über den Pool
 * je Experte im Zeitraum der Seite, am Berliner Buchungstag.
 */
const getSprechstunde = async (
  params: CockpitParams,
): Promise<{ data: CockpitSprechstundeResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitSprechstundeResponse }>(
      '/dashboard/cockpit/sprechstunde',
      { params: cockpitParams(params) },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Sektion „Terminverteilung" (Auftrag Alex 06.10.2026): anstehende Termine
 * eines Typs (EB/FB/KB/Service, Alex 07.10.2026) der nächsten 28 Tage. KEIN
 * Zeitraum — der Seitenfilter wirkt hier bewusst nicht. Bei EB kommen alle
 * vier Herkunfts-Varianten in einer Antwort.
 */
const getTerminverteilung = async (
  params: { scope: CockpitParams['scope']; typ: CockpitTerminverteilungTyp } & CockpitInaktivSchalter,
): Promise<{ data: CockpitTerminverteilungResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitTerminverteilungResponse }>(
      '/dashboard/cockpit/terminverteilung',
      { params: { scope: params.scope, typ: params.typ, ...inaktivParam(params) } },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Aufklappbare Liste zu einer Kachel — nur fuer die drei Warn-/Listen-Kacheln. */
const getDrilldown = async (
  key: CockpitDrilldownKey,
  params: CockpitParams,
): Promise<{ data: CockpitDrilldownResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitDrilldownResponse }>(
      `/dashboard/cockpit/drilldown/${key}`,
      { params: cockpitParams(params) },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Sparkline-Serien + Vorperioden-Vergleich (Auftrag Alex 09.08.2026, Punkt 2).
 *
 * Eigener Aufruf statt Teil der Sektionen: der Verlauf ist teurer (gemessen
 * ~1,7 s auf dev bei 90 Tagen) und soll die Kacheln nicht aufhalten — die
 * stehen sofort, die Trends kommen nach.
 */
const getVerlauf = async (
  params: CockpitVerlaufParams,
): Promise<{ data: CockpitVerlaufResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitVerlaufResponse }>(
      '/dashboard/cockpit/verlauf',
      {
        params: {
          ...cockpitParams(params),
          ...(params.granularitaet ? { granularitaet: params.granularitaet } : {}),
        },
      },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Team-Drilldown einer Kachel (Teil C, 09.08.2026): je Team bzw. je Mitglied. */
const getAufschluesselung = async (
  params: CockpitAufschluesselungParams,
): Promise<{ data: CockpitAufschluesselung }> => {
  try {
    const { data } = await network.get<{ data: CockpitAufschluesselung }>(
      '/dashboard/cockpit/aufschluesselung',
      {
        params: {
          ...cockpitParams(params),
          ...(params.ebene ? { ebene: params.ebene } : {}),
          ...(params.kategorie ? { kategorie: params.kategorie } : {}),
          // `null` MUSS mitgehen: auf Mitglieder-Ebene heisst es „die
          // Teamlosen" und ist nicht dasselbe wie „weggelassen".
          ...(params.ebene === 'mitglied' ? { team_id: params.teamId ?? '' } : {}),
        },
      },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Bereich „Telefonie" — Showquoten je Telefonie-Team (Auftrag Alex 20.08.2026).
 *
 * 🔥 EIGENE PARAMETER, KEIN `cockpitParams`: der Bereich rechnet auf MONATEN
 * (`monat=YYYY-MM`) und nicht auf dem Seiten-Zeitraum, und er kennt keinen
 * Scope — er verlangt die Rolle `sa` und zeigt dann alles. Die gemeinsame
 * Hilfsfunktion haette hier `from`/`to` mitgeschickt, die es serverseitig
 * nicht gibt.
 *
 * ⚠ Nur fuer SA: fuer alle anderen antwortet der Endpunkt mit 403. Das
 * Frontend rendert die Sektion deshalb gar nicht erst (LeadershipDashboard).
 */
const getTelefonie = async (
  params: CockpitTelefonieParams,
): Promise<{ data: CockpitTelefonieResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitTelefonieResponse }>(
      '/dashboard/cockpit/telefonie',
      {
        // `cockpitParams` statt eigener Zusammenbau: der Bereich hängt seit
        // dem 23.08.2026 am Seitenfilter und schickt denselben Zeitraum wie
        // die übrigen Sektionen. Ein zweiter Parameterbau wäre die Stelle, an
        // der die beiden Zeiträume auseinanderlaufen.
        params: { ...cockpitParams(params), ...(params.team ? { team: params.team } : {}) },
      },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Sektion „Rekla-Quote je Telefonagent" (Auftrag Alex 10.09.2026).
 *
 * Eigener Endpunkt neben `getTelefonie`, obwohl beide „Telefonie" heissen —
 * und das ist kein Doppel: die beiden SICHTBARKEITEN sind verschieden. Die
 * Showquote verlangt die Rolle `sa`, diese Sektion `dashboard.leadership.view`
 * wie die übrigen Cockpit-KPIs (Vorgabe Alex). Ein gemeinsamer Endpunkt hätte
 * die strengere Schranke für beide bedeutet.
 *
 * 🔥 DESHALB LÄDT SIE GETRENNT: ein 403 der Showquote darf diese Sektion nicht
 * mitreissen — eine Führungskraft ohne SA sieht genau diese hier und die
 * andere nicht.
 */
const getReklaquote = async (
  params: CockpitReklaquoteParams,
): Promise<{ data: CockpitReklaquoteResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitReklaquoteResponse }>(
      '/dashboard/cockpit/reklaquote',
      {
        // `cockpitParams` wie alle Sektionen — der Zeitraum ist der Seitenfilter.
        params: { ...cockpitParams(params), ...(params.team ? { team: params.team } : {}) },
      },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Rookie-Telefonie + Zoom-Kundenzeit (W3, 21.09.2026). EIGENE Zeitachse
 * (zeitraum + datum) statt from/to — die Sektion haengt bewusst nicht am
 * Seitenfilter (Task-Datei §1: Tag / ISO-Woche / Monat).
 */
const rookieTelefonieParams = (params: CockpitRookieTelefonieParams) => ({
  zeitraum: params.zeitraum,
  datum: params.datum,
  ...(params.team ? { team: params.team } : {}),
  ...(params.gruppe ? { gruppe: params.gruppe } : {}),
  ...inaktivParam(params),
});

const getRookieTelefonie = async (
  params: CockpitRookieTelefonieParams,
): Promise<{ data: CockpitRookieTelefonieResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitRookieTelefonieResponse }>(
      '/dashboard/cockpit/rookie-telefonie',
      { params: rookieTelefonieParams(params) },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getRookieTelefonieVerlauf = async (
  params: CockpitRookieTelefonieParams & { userId: number },
): Promise<{ data: CockpitRookieTelefonieVerlaufResponse }> => {
  try {
    const { userId, ...rest } = params;
    const { data } = await network.get<{ data: CockpitRookieTelefonieVerlaufResponse }>(
      '/dashboard/cockpit/rookie-telefonie/verlauf',
      { params: { ...rookieTelefonieParams(rest), user_id: userId } },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Kachel „NoShow VP" (Auftrag Alex 01.10.2026). Eigener Endpunkt mit eigener
 * Sicht: die Liste grenzt wie das Manager-Board auf den Teambaum der
 * Fuehrungskraft ein (SA: alles) — die uebrigen Cockpit-Kacheln fragen mit
 * `scope=all`. Deshalb KEIN `cockpitParams`: ein `scope` hat hier keine
 * Bedeutung, nur der Zeitraum und optional ein Team.
 */
const getNoShowVp = async (
  params: CockpitNoShowVpParams,
): Promise<{ data: CockpitNoShowVpResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitNoShowVpResponse }>(
      '/dashboard/cockpit/noshow-vp',
      { params: { from: params.from, to: params.to, ...(params.team != null ? { team: params.team } : {}), ...inaktivParam(params) } },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Die Fälle EINES Beraters — geladen erst beim Klick auf seine Zeile. */
const getNoShowVpFaelle = async (
  params: { from: string; to: string; userId: number },
): Promise<{ data: CockpitNoShowVpFaelleResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitNoShowVpFaelleResponse }>(
      `/dashboard/cockpit/noshow-vp/${params.userId}`,
      { params: { from: params.from, to: params.to } },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Sektion „Meta-Kampagnen" (Meta-Leads S3, nur SA). Eigener Zeitraum — der
 * Seitenfilter wirkt hier nicht. Seit S3e (E31b) als `from`/`to` (Tagesdatum,
 * Berliner Zeit, inklusive, höchstens 366 Tage). Liefert den ganzen Baum in
 * einer Antwort (Aufklappen ohne Nachladen, E28); `ansicht` ist seit S3f
 * zurückgebaut (E32a).
 */
const getMetaKampagnen = async (
  params: { from: string; to: string } & CockpitInaktivSchalter,
): Promise<{ data: CockpitMetaKampagnenResponse }> => {
  try {
    const { data } = await network.get<{ data: CockpitMetaKampagnenResponse }>(
      '/dashboard/cockpit/meta-kampagnen',
      { params: { from: params.from, to: params.to, ...inaktivParam(params) } },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const cockpitApi = {
  getMetaKampagnen,
  getAufschluesselung,
  getMeetings,
  getCases,
  getEbVerteilung,
  getAntraege,
  getBelieferung,
  getBuchungsquellen,
  getCloserRookie,
  getKalenderauslastung,
  getSprechstunde,
  getTerminverteilung,
  getDrilldown,
  getVerlauf,
  getTelefonie,
  getReklaquote,
  getRookieTelefonie,
  getRookieTelefonieVerlauf,
  getNoShowVp,
  getNoShowVpFaelle,
};

export default cockpitApi;
